import type { AdminBillingManager, AdminCompanyRow } from '@/state/useAdminBilling';
import type { UseDataControlReturn } from '@/components/DataTable';
import { BILLING_PLAN_CATALOGUE, type BillingPlan } from 'definitions/billing-plans';
import { Button } from '@/components/ui/button';
import { DataTable, useDataControl } from '@/components/DataTable';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import { SearchInput } from '@/components/SearchInput';
import { cn } from 'helpers/cn';
import { canChangePlan, useAdminBilling } from '@/state/useAdminBilling';
import { useAdminCompanyForm } from '@/state/useAdminCompanyForm';
import { formatBillingDate, formatUnits, statusLabel } from '@/state/billing/billing-format';
import { ActionButton, EmptyState, MICRO_LABEL_SHAPE, Panel, SEARCH_FIELD_CLASS } from '../../Dashboard/ConsolePanel';
import { SectionStatusLine } from '../../Dashboard/Settings/Billing/SectionStatusLine';
import { BillingModeControl, MODE_LABEL } from './BillingModeControl';
import { ChangePlanDialog } from './ChangePlanDialog';
import { CreateCompanyDialog } from './CreateCompanyDialog';
import { ExtendTrialDialog } from './ExtendTrialDialog';
import { DiscountsPanel } from './DiscountsPanel';

/** "Grow, yearly"; the plan column for a row with one. */
const planLabel = (item: AdminCompanyRow): string => {
  const plan = item.plan as BillingPlan | null;
  const name = plan && plan in BILLING_PLAN_CATALOGUE ? BILLING_PLAN_CATALOGUE[plan].name : plan;
  if (!name) return '-';
  return item.interval ? `${name}, ${item.interval === 'year' ? 'yearly' : 'monthly'}` : name;
};

/**
 * Superadmin billing overview: every company, its billing_mode, plan, subscription
 * status, month-to-date website sessions, and trial end. Row actions cover the mode,
 * the plan of an account Polar does not bill, extending a live trial, and the usage
 * report CSV. The panel header creates a company billed outside Polar and invites its owner.
 *
 * No GrantCreditsDialog here: see admin-billing/index.ts. The installed Polar
 * SDK has no admin-callable "credit this customer" endpoint.
 */
export const Billing = () => {
  const manager = useAdminBilling();
  const { companies } = manager;
  const companyForm = useAdminCompanyForm({ createCompany: manager.createCompany });
  // Up here rather than in the table so the search can sit in the panel's header.
  const dataController = useDataControl<AdminCompanyRow>(companies.data?.companies ?? []);
  const { searchQuery, setSearchQuery } = dataController;
  const hasCompanies = (companies.data?.companies.length ?? 0) > 0;

  return (
    <div className="flex flex-col gap-[14px]">
      <Panel
        title="Companies"
        description="Billing mode, subscription and month-to-date usage for every company."
        actions={
          <>
            {hasCompanies && (
              <SearchInput
                placeholder="Search by name or ID"
                value={searchQuery}
                handleSearch={setSearchQuery}
                className={SEARCH_FIELD_CLASS}
              />
            )}
            <Button size="sm" className="h-8 rounded-lg text-body" onClick={companyForm.open}>
              Create company
            </Button>
          </>
        }
      >
        {!companies.data ? (
          <SectionStatusLine
            status={companies}
            loadingText="Loading companies..."
            errorText="Companies could not be loaded."
          />
        ) : companies.data.companies.length === 0 ? (
          <EmptyState>No companies yet.</EmptyState>
        ) : (
          <CompaniesTable dataController={dataController} manager={manager} />
        )}
      </Panel>

      <DiscountsPanel manager={manager} />

      <ExtendTrialDialog manager={manager} />
      <ModeChangeDialog manager={manager} />
      <ChangePlanDialog manager={manager} />
      <CreateCompanyDialog form={companyForm} />
    </div>
  );
};

type CompaniesTableProps = { dataController: UseDataControlReturn<AdminCompanyRow>; manager: AdminBillingManager };

const CompaniesTable = ({ dataController, manager }: CompaniesTableProps) => (
  <DataTable<AdminCompanyRow>
    dataController={dataController}
    searchKeys={['name', 'id']}
    uniqueKey="id"
    columns={[
      { label: 'Company', field: 'name', sortKey: 'name', mobile: 'title' },
      { label: 'Type', field: 'type', format: ({ item }) => item.type ?? '-' },
      {
        label: 'Billing mode',
        format: ({ item }) => <BillingModeControl company={item} manager={manager} />,
      },
      { label: 'Plan', field: 'plan', sortKey: 'plan', format: ({ item }) => planLabel(item) },
      {
        label: 'Subscription',
        field: 'subscriptionStatus',
        format: ({ item }) =>
          item.subscriptionStatus ? (
            <span
              className={cn(
                MICRO_LABEL_SHAPE,
                'rounded-pill inline-flex w-fit border px-2 py-0.5',
                item.hasLiveSubscription ? 'border-ok-line text-ok' : 'border-line text-dim',
              )}
            >
              {statusLabel(item.subscriptionStatus)}
            </span>
          ) : (
            '-'
          ),
      },
      {
        label: 'Trial end',
        field: 'trialEnd',
        sortKey: 'trialEnd',
        format: ({ item }) => <span className="num">{formatBillingDate(item.trialEnd)}</span>,
      },
      {
        label: 'Sessions this month',
        field: 'sessionsThisMonth',
        sortKey: 'sessionsThisMonth',
        format: ({ item }) => <span className="num">{formatUnits(item.sessionsThisMonth)}</span>,
      },
      {
        label: ' ',
        mobile: 'action',
        format: ({ item }) => (
          <div className="flex flex-wrap gap-2">
            {/* Only a live card subscription has a trial Polar can move. */}
            {item.billingMode === 'card' && item.hasLiveSubscription && (
              <ActionButton onClick={() => manager.openExtendTrial(item)} ariaLabel={`Extend trial for ${item.name}`}>
                Extend trial
              </ActionButton>
            )}
            {/* A plan Polar does not hold is a label, changed here; a card plan is changed through Polar. */}
            {canChangePlan(item) && (
              <ActionButton onClick={() => manager.openChangePlan(item)} ariaLabel={`Change plan for ${item.name}`}>
                Change plan
              </ActionButton>
            )}
            {/* By how it is billed, so a card-mode client of an invoice agency is reported like the invoice account it is. */}
            {item.effectiveBillingMode === 'invoice' && (
              <ActionButton
                onClick={() => manager.downloadUsageReport(item)}
                ariaLabel={`Download this month's usage report for ${item.name}`}
              >
                Usage report
              </ActionButton>
            )}
          </div>
        ),
      },
    ]}
  />
);

type ModeChangeDialogProps = { manager: AdminBillingManager };

/** The one confirm for a mode change that has a consequence; see PendingModeChange. */
const ModeChangeDialog = ({ manager }: ModeChangeDialogProps) => {
  const { pendingModeChange: pending } = manager;
  const modeLabel = pending ? (MODE_LABEL[pending.mode] ?? pending.mode) : '';

  return (
    <ConfirmDialog
      open={!!pending}
      title={pending?.reason === 'revoke' ? 'Revoke the live subscription?' : 'Move to card billing?'}
      confirmLabel={pending?.reason === 'revoke' ? 'Switch and revoke' : 'Switch to card'}
      confirmVariant={pending?.reason === 'revoke' ? 'destructive' : 'default'}
      onConfirm={manager.confirmModeChange}
      onCancel={manager.cancelModeChange}
    >
      {pending?.reason === 'revoke' ? (
        <p className="text-foreground m-0 text-body">
          {pending.company.name} has a live subscription
          {pending.company.subscriptionStatus ? ` (${statusLabel(pending.company.subscriptionStatus)})` : ''}. Switching
          to <strong>{modeLabel}</strong> revokes it in Polar immediately. This cannot be undone from here.
        </p>
      ) : (
        <p className="text-foreground m-0 text-body">
          On card billing, {pending?.company.name}'s websites stop serving experiments and surveys until it subscribes
          with a card.
        </p>
      )}
    </ConfirmDialog>
  );
};
